/**
 * Colorado calendar-age math. Lifted from
 * `apps/customer-web/src/lib/colorado-calendar-age.ts` so customer-mobile
 * can drop its near-duplicate at `apps/customer-mobile/src/lib/colorado-age.ts`.
 *
 * Aligns with API `getColoradoPurchaseDateString` / `COLORADO_TIMEZONE`
 * (`@the-drop/types`).
 */
export const COLORADO_TIMEZONE = "America/Denver";
const MIN_RECREATIONAL_AGE = 21;
/** Colorado calendar `YYYY-MM-DD` for `now` (Denver civil date). */
export function getColoradoTodayYmd(now = new Date()) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: COLORADO_TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(now);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    if (!y || !m || !d) {
        throw new Error("colorado_today_unresolved");
    }
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}
/**
 * Full years of age on calendar `asOfYmd`, same algorithm as API
 * `computeAgeInFullYearsOnCalendarDate`.
 */
export function computeAgeInFullYearsOnCalendarDate(dateOfBirthYmd, asOfYmd) {
    const dobParts = dateOfBirthYmd.split("-").map(Number);
    const refParts = asOfYmd.split("-").map(Number);
    if (dobParts.length !== 3 || refParts.length !== 3) {
        throw new Error("invalid_ymd_format");
    }
    const y = dobParts[0];
    const m = dobParts[1];
    const d = dobParts[2];
    const ry = refParts[0];
    const rm = refParts[1];
    const rd = refParts[2];
    if ([y, m, d, ry, rm, rd].some((n) => Number.isNaN(n))) {
        throw new Error("invalid_ymd_format");
    }
    let age = ry - y;
    if (rm < m || (rm === m && rd < d)) {
        age -= 1;
    }
    return age;
}
export function isAtLeastRecreationalAgeColorado(dateOfBirthYmd, now = new Date()) {
    const today = getColoradoTodayYmd(now);
    const age = computeAgeInFullYearsOnCalendarDate(dateOfBirthYmd, today);
    return age >= MIN_RECREATIONAL_AGE;
}
export function minRecreationalAge() {
    return MIN_RECREATIONAL_AGE;
}
//# sourceMappingURL=colorado-calendar-age.js.map